import { useState } from "react";
import { Alert, App, Button, Card, Descriptions, Drawer, Popconfirm, Select, Space, Spin, Tabs, Tag } from "antd";
import { Link } from "react-router-dom";
import { EditOutlined, ExportOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api/client";
import { useFactory, useLinkFactory, useUnlinkFactory } from "../../api/factories";
import { useSuppliersList } from "../../api/audits";
import { ManualMark, Rating, fmtD, fmtN } from "../pills";
import { ManualValueModal, type ManualTarget } from "../supplier/ManualValueModal";
import { Sparkline } from "../charts/Charts";
import { useFullDrawer } from "../audit/mobileModal";
import { FactoryFormModal } from "./FactoryFormModal";
import { FactoryName, FactoryStatusTag } from "./FactoryTable";
import { monoStyle } from "../ResponsiveList";
import { FactoryAudits } from "./FactoryAudits";
import { FactoryNcs } from "./FactoryNcs";
import { VendorhubData } from "./VendorhubData";

/** Drawer: anteprima della pagina dello stabilimento (/factories/:id), con «Apri pagina». */
export function FactoryDrawer(props: { factoryId: string | null; supplierId?: string; onClose: () => void; onNewAudit?: (factoryId: string) => void }) {
  const { factoryId, supplierId, onClose, onNewAudit } = props;
  const { t } = useTranslation();
  const fd = useFullDrawer(900);
  const q = useFactory(factoryId);
  const f = q.data;
  return (
    <Drawer
      {...fd}
      open={!!factoryId}
      onClose={onClose}
      title={f ? <FactoryName f={f} /> : t("f_factory")}
      destroyOnHidden
      extra={
        factoryId ? (
          <Link to={`/factories/${factoryId}${supplierId ? `?s=${supplierId}` : ""}`} onClick={onClose}>
            <Button size="small" icon={<ExportOutlined />}>{t("f_openPage")}</Button>
          </Link>
        ) : null
      }
    >
      {factoryId && <FactoryView factoryId={factoryId} supplierId={supplierId} onLeave={onClose} onNewAudit={onNewAudit} />}
    </Drawer>
  );
}

/** Stabilimento: scheda «Audit» (una riga per audit, con report ed evidenze) e scheda anagrafica. Nel drawer e nella pagina. */
export function FactoryView(props: { factoryId: string; supplierId?: string; onLeave?: () => void; onNewAudit?: (factoryId: string) => void }) {
  const { factoryId, supplierId, onLeave, onNewAudit } = props;
  const onClose = () => onLeave?.();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { me, can } = useAuth();
  const isSup = me?.role === "supplier";
  const q = useFactory(factoryId);
  const link = useLinkFactory();
  const unlink = useUnlinkFactory();
  const suppliers = useSuppliersList();
  const [edit, setEdit] = useState(false);
  const [manual, setManual] = useState<ManualTarget | null>(null);
  const [linkTo, setLinkTo] = useState<string | undefined>();
  const [tab, setTab] = useState("aud");
  const f = q.data;
  const editable = can("supplier.edit") && !isSup;
  const spark = (f?.scoreHistory ?? []).filter((h) => h.total != null).map((h) => h.total as number);
  const fail = (e: unknown) => void message.error(e instanceof ApiError ? e.message : String(e));

  return (
    <div>
      {q.isLoading && <Spin style={{ display: "block", margin: 32 }} />}
      {q.error && <Alert type="error" showIcon message={q.error instanceof ApiError ? q.error.message : t("noResults")} />}
      {f && (
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: "aud",
              label: `${t("audits")} (${f.audits.length})`,
              children: (
                <div style={{ display: "grid", gap: 12 }}>
                  {onNewAudit && can("audit.create") ? (
                    <div>
                      <Button type="primary" onClick={() => onNewAudit(f.id)}>{t("newAudit")}</Button>
                    </div>
                  ) : null}
                  <FactoryAudits factoryId={f.id} supplierId={supplierId ?? f.suppliers[0]?.id ?? ""} />
                </div>
              ),
            },
            { key: "ncs", label: t("ncs"), children: <FactoryNcs factoryId={f.id} onLeave={onLeave} /> },
            { key: "info", label: t("f_info"), children: null },
          ]}
          tabBarExtraContent={editable ? <Button size="small" onClick={() => setEdit(true)}>{t("edit")}</Button> : null}
          style={{ marginTop: -8 }}
        />
      )}
      {f && tab === "info" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Card size="small">
            <Space size={12} wrap align="center">
              <Rating score={f.rating} override={f.ratingOverride} />
              <span style={{ fontSize: 24, fontWeight: 800 }}>{fmtN(f.rating, 1)}</span>
              <span style={{ opacity: 0.6 }}>{f.ratingAt ? `${t("f_ratingAt")} ${fmtD(f.ratingAt)}` : t("f_noRating")}</span>
              {f.ratingManual && <ManualMark note={f.ratingNote} />}
              {can("admin") && !isSup && (
                <Button size="small" type="text" icon={<EditOutlined />} aria-label={t("m_setRating")} onClick={() => setManual({ kind: "rating", entity: "factories", id: f.id, name: f.name, rating: f.rating, manual: f.ratingManual })} />
              )}
              {!f.active && <Tag>{t("act_inactive")}</Tag>}
              {can("admin") && !isSup && (
                <Button size="small" onClick={() => setManual({ kind: "active", entity: "factories", id: f.id, name: f.name, active: f.active })}>{f.active ? t("act_deactivate") : t("act_reactivate")}</Button>
              )}
              <FactoryStatusTag s={f.status} />
              <Tag>{t("f_vendorRating")}: {f.grade && f.grade !== "Unassigned" ? f.grade : t("f_unassigned")}</Tag>
            </Space>
            {spark.length > 1 && <div style={{ marginTop: 8 }}><Sparkline values={spark} /></div>}
          </Card>
          <Descriptions column={1} size="small" bordered styles={{ label: { width: 170 }, content: { overflowWrap: "anywhere" } }}>
            <Descriptions.Item label={t("sapCode")}><span style={monoStyle}>{f.code ?? "—"}</span>{f.displayCode ? <span style={{ opacity: 0.6 }}> · {f.displayCode}</span> : null}</Descriptions.Item>
            {f.nameLocal && <Descriptions.Item label={t("f_nameLocal")}>{f.nameLocal}</Descriptions.Item>}
            <Descriptions.Item label={t("f_address")}><span data-sensitive>{[f.address, f.city, f.country].filter(Boolean).join(", ") || "—"}</span></Descriptions.Item>
            <Descriptions.Item label={t("f_tier")}>{f.tier === 3 ? t("f_tier3") : t("f_tier2")}</Descriptions.Item>
            {f.businessUnits.length > 0 && <Descriptions.Item label={t("f_bu")}>{f.businessUnits.join(", ")}</Descriptions.Item>}
            {f.brands.length > 0 && <Descriptions.Item label={t("f_brands")}>{f.brands.map((b) => <Tag key={b}>{b}</Tag>)}</Descriptions.Item>}
            <Descriptions.Item label={t("f_suppliers")}>
              <div style={{ display: "grid", gap: 4 }}>
                {f.suppliers.map((s) => (
                  <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Link to={`/suppliers/${s.id}`} onClick={onClose}>{s.name}</Link>
                    <span style={{ ...monoStyle, opacity: 0.6 }}>{s.code}</span>
                    {s.relationshipType && <Tag>{s.relationshipType}</Tag>}
                    {editable && f.suppliers.length > 1 && (
                      <Popconfirm title={t("f_unlinkQ")} onConfirm={() => unlink.mutateAsync({ id: f.id, supplierId: s.id }).catch(fail)}>
                        <Button size="small" type="link" danger>{t("f_unlink")}</Button>
                      </Popconfirm>
                    )}
                  </div>
                ))}
                {editable && (
                  <Space.Compact style={{ maxWidth: 420 }}>
                    <Select
                      style={{ minWidth: 260 }}
                      placeholder={t("f_linkPh")}
                      value={linkTo}
                      onChange={setLinkTo}
                      showSearch
                      optionFilterProp="label"
                      options={(suppliers.data ?? []).filter((s) => !f.suppliers.some((x) => x.id === s.id)).map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))}
                    />
                    <Button disabled={!linkTo} loading={link.isPending} onClick={() => linkTo && link.mutateAsync({ id: f.id, supplierId: linkTo }).then(() => setLinkTo(undefined)).catch(fail)}>{t("f_link")}</Button>
                  </Space.Compact>
                )}
              </div>
            </Descriptions.Item>
          </Descriptions>
          {!isSup && <div data-sensitive><VendorhubData data={f.vendorhubData} /></div>}
          {!isSup && f.contacts.length > 0 && (
            <Card size="small" title={t("f_contacts")}>
              {f.contacts.map((c) => (
                <div key={c.id} style={{ marginBottom: 4 }} data-sensitive>
                  <b>{c.name}</b> {c.isPrimary && <Tag color="blue">{t("f_primary")}</Tag>}
                  <span style={{ opacity: 0.65 }}> {[c.title || c.role, c.email, c.phone].filter(Boolean).join(" · ")}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
      <FactoryFormModal open={edit} factory={f} onClose={() => setEdit(false)} />
      <ManualValueModal target={manual} onClose={() => setManual(null)} />
    </div>
  );
}
